import { useState } from "react";
import { Button, Card, InputText, Stack, Text } from "@bookingcom/bui-react";
import { HTTPError } from "ky";
import { useCreateAirlineMutation } from "../../hooks/useCreateAirlineMutation";
import styles from "./AddAirlineForm.module.css";

type AddAirlineFormProps = {
  onSuccess: (airlineCode: string, airlineName: string) => void;
};

const AIRLINE_CODE_PATTERN = /^[A-Z0-9]{2}$/;
const EMAIL_DOMAIN_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9-]*(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

export const AddAirlineForm = ({ onSuccess }: AddAirlineFormProps) => {
  const [airlineName, setAirlineName] = useState("");
  const [airlineCode, setAirlineCode] = useState("");
  const [emailDomains, setEmailDomains] = useState<string[]>([""]);

  const [nameError, setNameError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [domainErrors, setDomainErrors] = useState<string[]>([""]);
  const [serverError, setServerError] = useState("");

  const { mutate, isPending } = useCreateAirlineMutation();

  const validate = (): boolean => {
    let valid = true;

    if (!airlineName.trim()) {
      setNameError("Airline name is required");
      valid = false;
    } else {
      setNameError("");
    }

    if (!AIRLINE_CODE_PATTERN.test(airlineCode)) {
      setCodeError("IATA code must be exactly 2 uppercase alphanumeric characters");
      valid = false;
    } else {
      setCodeError("");
    }

    const newDomainErrors = emailDomains.map((domain) => {
      if (!domain.trim()) return "Email domain is required";
      if (!EMAIL_DOMAIN_PATTERN.test(domain.trim())) return "Please enter a valid email domain";
      return "";
    });
    setDomainErrors(newDomainErrors);
    if (newDomainErrors.some((e) => e !== "")) valid = false;

    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setServerError("");
    const domains = emailDomains.map((d) => d.trim());

    mutate(
      { airlineCode, airlineName: airlineName.trim(), emailDomains: domains, isActive: true },
      {
        onSuccess: () => onSuccess(airlineCode, airlineName.trim()),
        onError: async (error) => {
          if (error instanceof HTTPError) {
            try {
              const body = await error.response.json<{ error: string }>();
              setServerError(body.error);
            } catch {
              setServerError("Something went wrong. Please try again.");
            }
          } else {
            setServerError("Something went wrong. Please try again.");
          }
        },
      }
    );
  };

  const handleDomainChange = (index: number, value: string) => {
    const updated = [...emailDomains];
    updated[index] = value;
    setEmailDomains(updated);
    if (domainErrors[index]) {
      const updatedErrors = [...domainErrors];
      updatedErrors[index] = "";
      setDomainErrors(updatedErrors);
    }
  };

  const addDomain = () => {
    setEmailDomains([...emailDomains, ""]);
    setDomainErrors([...domainErrors, ""]);
  };

  const removeDomain = (index: number) => {
    setEmailDomains(emailDomains.filter((_, i) => i !== index));
    setDomainErrors(domainErrors.filter((_, i) => i !== index));
  };

  const isSubmitDisabled =
    !airlineName.trim() || !airlineCode || emailDomains.every((d) => !d.trim()) || isPending;

  return (
    <Card mixin={{ padding: 6 }}>
      <Stack gap={4} className={styles.airlineForm}>
        <Stack gap={1}>
          <Text variant="emphasized_2">Airline name</Text>
          <InputText
            name="airlineName"
            size="large"
            placeholder="e.g. British Airways"
            value={airlineName}
            onChange={({ value }: { value: string }) => {
              setAirlineName(value);
              if (nameError) setNameError("");
            }}
            error={nameError || undefined}
          />
        </Stack>

        <Stack gap={1}>
          <Text variant="emphasized_2">IATA code</Text>
          <InputText
            name="airlineCode"
            size="large"
            placeholder="e.g. BA"
            value={airlineCode}
            onChange={({ value }: { value: string }) => {
              setAirlineCode(value.toUpperCase());
              if (codeError) setCodeError("");
            }}
            error={codeError || undefined}
          />
        </Stack>

        <Stack gap={2}>
          <Text variant="emphasized_2">Email domains</Text>
          {emailDomains.map((domain, index) => (
            <Stack key={index} direction="row" gap={2} alignItems="start">
              <InputText
                name={`emailDomain-${index}`}
                size="large"
                prefix="@"
                placeholder="e.g. britishairways.com"
                value={domain}
                onChange={({ value }: { value: string }) => handleDomainChange(index, value)}
                error={domainErrors[index] || undefined}
              />
              {emailDomains.length > 1 && (
                <Button variant="tertiary" size="large" onClick={() => removeDomain(index)}>
                  Remove
                </Button>
              )}
            </Stack>
          ))}
          <Button variant="secondary" size="medium" onClick={addDomain}>
            Add another domain
          </Button>
        </Stack>

        {serverError && (
          <Text variant="body_2" color="destructive">
            {serverError}
          </Text>
        )}

        <Button size="large" onClick={handleSubmit} disabled={isSubmitDisabled}>
          Add airline
        </Button>
      </Stack>
    </Card>
  );
};
